import { styled } from '@mui/material';
import { dynamicQuestionnaireSchema } from 'FeatureModules/Questionnaire/schemas/questionnaire.schema';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { Form, Formik, FormikProps } from 'formik';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import React, { FC, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { EPartnerRequestStatus } from 'FeatureModules/Partner/types/partner.types';
import {
  EQuestionType,
  IQuestionnaireCompanyResponse,
  IQuestionnaireFormValues,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';
import Question from './Question';
import Moment from 'react-moment';
import FlexBox from 'Components/FlexBox';

interface IQuestionnaireFormProps {
  response: IQuestionnaireCompanyResponse;
  actionButtonsContainer?: RefObject<HTMLElement | null>;
  saving: boolean;
  submitting: boolean;
  /**
   * Call the save function every x seconds with changed values if any values in the form changed
   * Default: 5 seconds
   */
  autoSaveEverySeconds?: number;
  /**
   * Only changed values will be sent into the save function
   */
  onSaveClick: (changedValues: IQuestionnaireFormValues) => void;
  /**
   * If nothing changed the values will be undefined and the save function will not be called
   */
  onFinishClick: (changedValues?: IQuestionnaireFormValues) => void;
}

const Section = styled(FlexBox)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: theme.spacing(2),
  borderRadius: 6,
  background: theme.custom.themeColors.grayScale[5],
}));

const SavedContainer = styled(FlexBox)(({ theme }) => ({
  padding: '6px 12px',
  gap: '10px',
  borderRadius: '6px',
  background: theme.custom.themeColors.grayScale[10],
}));

const Tag = styled(ThemeTypography)(({ theme }) => ({
  borderRadius: 20,
  padding: theme.spacing(1, 2),
  background: theme.custom.themeColors.grayScale[20],
}));

const StyledForm = styled(Form)(() => ({
  width: '100%',
}));

const QuestionnaireForm: FC<IQuestionnaireFormProps> = ({
  submitting,
  saving,
  actionButtonsContainer,
  autoSaveEverySeconds = 30,
  response: { sections, partnerRequest, modifiedTimestamp },
  onSaveClick,
  onFinishClick,
}) => {
  const formRef = useRef<FormikProps<IQuestionnaireFormValues>>(null);

  const isPendingRequest = partnerRequest?.requestStatus === EPartnerRequestStatus.REQUESTED;

  /**
   * This build a dynamic schema in the following structure
   * sections: {
   *   [sectionId]: {
   *     questions: {
   *       [questionId]: {
   *         answerText: string, # Available for short/long free text questions
   *         selectedOptionId: string, # Available for single selects and single dropdown questions
   *         selectedOptionIds: string[], # Available for multi selects and multi select dropdown questions
   *         documentIds: string[], # For document upload questions
   *         requiredCommentText: string # Available for all question types if any option required a comment
   *       }
   *     }
   *   }
   * }
   */
  const dynamicFormSchema = useMemo(
    () => dynamicQuestionnaireSchema(sections),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initialValues: IQuestionnaireFormValues = useMemo(
    () => ({
      sections: sections.reduce(
        (prev, section) => ({
          ...prev,
          [section.id]: {
            questions: {
              ...section.questions.reduce(
                (prevQuestions, question) => ({
                  ...prevQuestions,
                  [question.id]: {
                    ...(question.questionType === EQuestionType.SHORT_FREE_TEXT ||
                    question.questionType === EQuestionType.LONG_FREE_TEXT
                      ? { answerText: question.answerText }
                      : {}),

                    ...(question.questionType === EQuestionType.SINGLE_SELECT ||
                    question.questionType === EQuestionType.SINGLE_SELECT_DROP_DOWN
                      ? { selectedOptionId: question.selectedOptionIds?.[0] }
                      : {}),

                    ...(question.questionType === EQuestionType.MULTI_SELECT ||
                    question.questionType === EQuestionType.MULTI_SELECT_DROP_DOWN
                      ? { selectedOptionIds: question.selectedOptionIds || [] }
                      : {}),

                    ...(question.questionType === EQuestionType.DOCUMENTS
                      ? {
                          documents:
                            question.proofDocuments?.map(({ id, title }) => ({
                              id,
                              title,
                              inUploadContext: false,
                            })) || [],
                        }
                      : {}),

                    ...(question.questionType !== EQuestionType.SHORT_FREE_TEXT &&
                    question.questionType !== EQuestionType.LONG_FREE_TEXT
                      ? {
                          commentText: question.commentText,
                        }
                      : {}),
                  },
                }),
                {}
              ),
            },
          },
        }),
        {}
      ),
    }),
    [sections]
  );

  const getChangedSectionValues = useCallback(
    (
      initialSections: IQuestionnaireFormValues['sections'],
      newSections: IQuestionnaireFormValues['sections']
    ) =>
      Object.keys(newSections).reduce(
        (acc, sectionId) => {
          const sectionValues = newSections[sectionId];
          const initialSectionValues = initialSections[sectionId];

          const changedQuestions = Object.keys(sectionValues.questions).reduce(
            (questionAcc, questionId) => {
              const questionValues = sectionValues.questions[questionId];
              const initialQuestionValues = initialSectionValues.questions[questionId];

              if (!isEqual(questionValues, initialQuestionValues)) {
                questionAcc[questionId] = questionValues;
              }

              return questionAcc;
            },
            {} as IQuestionnaireFormValues['sections'][string]['questions']
          );

          if (Object.keys(changedQuestions).length > 0) {
            acc.sections[sectionId] = {
              questions: changedQuestions,
            };
          }

          return acc;
        },
        { sections: {} } as IQuestionnaireFormValues
      ),
    []
  );

  const handleSubmitForm = (values: IQuestionnaireFormValues) => {
    if (!isEqual(initialValues, values)) {
      const changedValues = getChangedSectionValues(initialValues.sections, values.sections);
      onFinishClick(changedValues);
    } else {
      onFinishClick(undefined);
    }
  };

  /**
   * Tries to focus the question with an error in the form
   */
  const focusNextError = () => {
    if (!formRef.current) return;

    if (!isEmpty(formRef.current.errors)) {
      const sectionErrors = formRef.current.errors.sections;
      const firstSectionIdWithError = Object.keys(sectionErrors || {})[0];
      const sectionError = sectionErrors?.[firstSectionIdWithError];
      if (sectionError && typeof sectionError === 'object' && !Array.isArray(sectionError)) {
        const sectionQuestionErrors = sectionError.questions;
        if (sectionQuestionErrors && typeof sectionQuestionErrors === 'object') {
          const firstQuestionIdWithError = Object.keys(sectionQuestionErrors)[0];
          const elementId = `sections.${firstSectionIdWithError}.questions.${firstQuestionIdWithError}`;
          const element = document.getElementById(elementId);
          if (element) {
            // Use setTimeout to ensure DOM updates are complete
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }
        }
      }
    }
  };

  const handleSave = useCallback(() => {
    const formRefCurrent = formRef.current;
    if (!formRefCurrent || isEqual(initialValues, formRefCurrent.values)) {
      return;
    }

    const changedValues = getChangedSectionValues(
      formRefCurrent.initialValues.sections,
      formRefCurrent.values.sections
    );

    onSaveClick(changedValues);
  }, [initialValues, onSaveClick, getChangedSectionValues]);

  useEffect(() => {
    if (!formRef.current) return;
    formRef.current.setSubmitting(submitting);
    formRef.current.validateForm();
  }, [formRef, submitting]);

  useEffect(() => {
    if (!autoSaveEverySeconds) return;

    const interval = setInterval(() => {
      handleSave();
    }, autoSaveEverySeconds * 1000);

    return () => clearInterval(interval);
  }, [autoSaveEverySeconds, handleSave]);

  return (
    <Formik
      innerRef={formRef}
      initialStatus={{
        disabled: !isPendingRequest,
      }}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={dynamicFormSchema}
      onSubmit={handleSubmitForm}
    >
      {({ values, isSubmitting, submitForm }) => (
        <StyledForm>
          {actionButtonsContainer?.current &&
            isPendingRequest &&
            createPortal(
              <>
                <ThemeButton
                  color="PRIMARY"
                  loading={saving}
                  disabled={isEqual(initialValues, values) || submitting}
                  onClick={handleSave}
                >
                  Save
                </ThemeButton>

                <ThemeButton
                  color="PRIMARY"
                  disabled={saving}
                  loading={isSubmitting || submitting}
                  type="submit"
                  startIcon={<Icon name="document-request-fulfilled" size="small" color="WHITE" />}
                  // If the form is valid, focus the next error
                  // Because the buttons are rendered in a portal, we need to ensure the submit is still working by using the Formik submit function
                  onClick={async () => {
                    await submitForm();
                    // the form isValid state can not be ready immediately after submit
                    if (formRef.current && !formRef.current.isValid) {
                      focusNextError();
                    }
                  }}
                >
                  Finish & Send self-assessment
                </ThemeButton>
              </>,
              actionButtonsContainer.current
            )}
          {/* Could have also used an FieldArray here but this would make the validation quite a lot harder */}
          {sections.map((section, sectionIndex) => (
            <Section key={section.id} width="100%">
              <FlexBox gap={2}>
                <Tag variant="BODY_MEDIUM_BOLD" color="BLUE_100">
                  {sectionIndex + 1} of {sections.length}
                </Tag>
                <ThemeTypography variant="TITLE_SMALL" color="GRAY_60">
                  {section.title}
                </ThemeTypography>
              </FlexBox>
              {section.description && (
                <ThemeTypography
                  variant="BODY_MEDIUM"
                  dangerouslySetInnerHTML={{ __html: section.description }}
                />
              )}
              {section.questions.map((question, questionIndex) => (
                <Question
                  key={question.id}
                  name={`sections.${section.id}.questions.${question.id}`}
                  sectionIndex={sectionIndex}
                  questionIndex={questionIndex}
                  question={question}
                />
              ))}
            </Section>
          ))}

          {partnerRequest?.requestStatus === EPartnerRequestStatus.REQUESTED && (
            <FlexBox justifyContent="flex-end" gap={2} mt={3}>
              <SavedContainer>
                <Icon name="check" color="gray-30" size="small" />
                <ThemeTypography variant="BODY_SMALL_BOLD" color="GRAY_80" autoOverflow>
                  Last saved{' '}
                  <Moment
                    calendar={{
                      sameDay: '[today] HH:mm:ss',
                      lastDay: '[yesterday] HH:mm',
                      lastWeek: 'YYYY-MM-DD HH:mm',
                      sameElse: 'YYYY-MM-DD HH:mm',
                    }}
                  >
                    {modifiedTimestamp}
                  </Moment>
                </ThemeTypography>
              </SavedContainer>

              <ThemeButton
                color="PRIMARY"
                loading={saving}
                disabled={isEqual(initialValues, values) || submitting}
                onClick={handleSave}
              >
                Save
              </ThemeButton>

              <ThemeButton
                color="PRIMARY"
                disabled={saving}
                loading={isSubmitting || submitting}
                type="submit"
                startIcon={<Icon name="document-request-fulfilled" size="small" color="WHITE" />}
                // If the form is valid, focus the next error
                // Because the buttons are rendered in a portal, we need to ensure the submit is still working by using the Formik submit function
                onClick={async () => {
                  await submitForm();
                  // the form isValid state can not be ready immediately after submit
                  if (formRef.current && !formRef.current.isValid) {
                    focusNextError();
                  }
                }}
              >
                Finish & Send self-assessment
              </ThemeButton>
            </FlexBox>
          )}
        </StyledForm>
      )}
    </Formik>
  );
};

export default QuestionnaireForm;
